import React, { useEffect, useState } from "react";
import axiosInstance from "../../axiosInstance";
import { URL } from "../../api.jsx";
import { toast, ToastContainer } from "react-toastify";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import getPrivilegeList from "../PrivilegeList.jsx";

const WalletList = () => {
  const privilegeList = getPrivilegeList();

  /* ===================== LIST STATE ===================== */
  const [wallets, setWallets] = useState([]);

  /* ===================== DROPDOWN STATE ===================== */
  const [companyDropdown, setCompanyDropdown] = useState([]);
  const [branchDropdown, setBranchDropdown] = useState([]);
  const navigate = useNavigate();

  /* ===================== PAGINATION ===================== */
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  /* ===================== FILTER STATE ===================== */
const [filterType, setFilterType] = useState("");
const [filterValue, setFilterValue] = useState("");

  /* ===================== TOPUP MODAL STATE ===================== */
  const [showTopupModal, setShowTopupModal] = useState(false);
  const [topupAmount, setTopupAmount] = useState("");
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [utrNumber, setUtrNumber] = useState("");
  const [accountHolderName, setAccountHolderName] = useState("");

  /* ===================== WITHDRAW MODAL STATE ===================== */
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawRemarks, setWithdrawRemarks] = useState("");
  const [selectedWithdrawWallet, setSelectedWithdrawWallet] = useState(null);

  /* ===================== LOCAL STORAGE VALUES ===================== */
  const isAdmin = localStorage.getItem("isAdmin");
  const companyCode = localStorage.getItem("companyCode");
  const branchCode = localStorage.getItem("branch_code");

  const showTopupButton =
    isAdmin === "1" || (companyCode && branchCode);

  /* ===================== FETCH COMPANY DROPDOWN ===================== */
  const fetchCompanyDropdown = async () => {
    try {
      const res = await axiosInstance.get(URL.COMPANY_LIST_BY_CODE);
      setCompanyDropdown(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Company dropdown error:", err);
      setCompanyDropdown([]);
    }
  };

  /* ===================== FETCH BRANCH DROPDOWN ===================== */
  const fetchBranchDropdown = async () => {
    try {
      const res = await axiosInstance.get(URL.COMPANY_BRANCH);
      setBranchDropdown(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Branch dropdown error:", err);
      setBranchDropdown([]);
    }
  };

  /* ===================== GET COMPANY NAME ===================== */
  const getCompanyName = (code) => {
    const company = companyDropdown.find((c) => c.id === code);
    return company ? company.value : "-";
  };

  const getBranchName = (branchCode, companyCode) => {
    const branch = branchDropdown.find(
      (b) =>
        b.branchCode === branchCode &&
        b.companyCode === companyCode
    );

    return branch ? branch.branchName : "-";
  };

  /* ===================== FETCH WALLET ===================== */
  const fetchWallets = async () => {
    try {

      const isAdmin = localStorage.getItem("isAdmin");
      const companyCode = localStorage.getItem("companyCode");
      const branchCode = localStorage.getItem("branch_code");

      const params = new URLSearchParams();

      if (isAdmin === "1") {
        params.append("isAdmin", 1);
      } else {
        if (companyCode) {
          params.append("companyCode", companyCode);
        }

        if (branchCode) {
          params.append("branchCode", branchCode);
        }
      }

      const url = `${URL.GET_WALLET}?${params.toString()}`;

      const res = await axiosInstance.get(url);

      if (Array.isArray(res.data)) {
        setWallets(res.data);
      } else if (res.data) {
        setWallets([res.data]);
      } else {
        setWallets([]);
      }

    } catch (error) {
      console.error("Wallet fetch error:", error);
      setWallets([]);
    }
  };

  /* ===================== RAISE TOPUP REQUEST ===================== */
  const raiseTopupRequest = async () => {
    try {

      const userId = localStorage.getItem("userId");

      const payload = {
        companyCode: selectedWallet.companyCode,
        branchCode: selectedWallet.branchCode,
        amount: Number(topupAmount),
        requestedBy: Number(userId),
        utrNumber: utrNumber,
        accountHolderName: accountHolderName
      };
            await axiosInstance.post(URL.URL_RAISE_REQUEST, payload);

      toast.success("Request Raised Successfully");

      setShowTopupModal(false);
      setTopupAmount("");

    } catch (err) {
      console.error(err);
      toast.error("Failed to raise request");
    }
  };

  /* ===================== RAISE WITHDRAWAL REQUEST ===================== */
  const raiseWithdrawRequest = async () => {
    try {

      const userId = localStorage.getItem("userId");

      const payload = {
        companyCode: selectedWithdrawWallet.companyCode,
        branchCode: selectedWithdrawWallet.branchCode,
        amount: Number(withdrawAmount),
        requestedBy: Number(userId),
        remarks: withdrawRemarks
      };

      await axiosInstance.post(URL.WITHDRAW_REQUEST, payload);

      toast.success("Withdrawal Request Raised Successfully");

      setShowWithdrawModal(false);
      setWithdrawAmount("");
      setWithdrawRemarks("");
      setSelectedWithdrawWallet(null);
      fetchWallets();

    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to raise withdrawal request");
    }
  };

  /* ===================== LOAD DATA ===================== */
  useEffect(() => {
    fetchWallets();
    fetchCompanyDropdown();
    fetchBranchDropdown();
  }, []);

  /* ===================== FILTERED DATA ===================== */
const filteredWallets = wallets.filter((wallet) => {

  if (!filterType || !filterValue) return true;

  if (filterType === "company") {
    return getCompanyName(wallet.companyCode)
      .toLowerCase()
      .includes(filterValue.toLowerCase());
  }

  if (filterType === "branch") {
    return getBranchName(wallet.branchCode, wallet.companyCode)
      .toLowerCase()
      .includes(filterValue.toLowerCase());
  }

  return true;
});

  /* ===================== PAGINATION ===================== */
  const totalPages = Math.ceil(filteredWallets.length / rowsPerPage);

  const paginatedWallets = filteredWallets.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  return (
    <div className="container">

      {/* ===================== HEADER ===================== */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3>Wallet Balance</h3>
      </div>

    {/* ===================== FILTER SECTION ===================== */}
<div className="card mb-3 p-3">

  <div className="d-flex justify-content-between align-items-end">

    <div className="row w-75">

      <div className="col-md-4">
        <label className="form-label">Filter By</label>
        <select
          className="form-select form-select-sm"
          value={filterType}
          onChange={(e) => {
            setFilterType(e.target.value);
            setFilterValue("");
          }}
        >
          <option value="">Select</option>
          <option value="company">Company Name</option>
          <option value="branch">Agency Name</option>
        </select>
      </div>

      {filterType && (
        <div className="col-md-4">
          <label className="form-label">Search</label>
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Type to search"
            value={filterValue}
            onChange={(e) => setFilterValue(e.target.value)}
          />
        </div>
      )}

    </div>

  <div className="d-flex gap-2">
    {companyCode && (!branchCode || branchCode === "null") && (
      <button
        className="btn btn-success btn-sm"
        onClick={() => navigate("/WalletTopUpRequest")}
      >
        Top UP Requests
      </button>
    )}

    {privilegeList.walletTransactionView && (
      <button
        className="btn btn-info btn-sm"
        onClick={() => navigate("/WalletTransactions")}
      >
        Transaction History
      </button>
    )}
  </div>

  </div>

</div>

      {/* ===================== TABLE ===================== */}
      <div className="table-responsive" style={{ overflowX: "auto", whiteSpace: "nowrap" }}>
        <table className="table tablestyle table-sm table-bordered table-hover align-middle">

          <thead className="table-dark">
            <tr>
              <th>S.No.</th>
              <th>Company Code</th>
              <th>Company Name</th>
              <th>Agency Code</th>
              <th>Agency Name</th>
              <th>Wallet Type</th>
              <th>Balance</th>
              {showTopupButton && <th>Raise Request</th>}
            </tr>
          </thead>

          <tbody>
            {paginatedWallets.length > 0 ? (
              paginatedWallets.map((wallet, index) => (
                <tr key={index}>

                  <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>

                  <td>{wallet.companyCode || "-"}</td>

                  <td>{getCompanyName(wallet.companyCode)}</td>

                  <td>{wallet.branchCode || "-"}</td>

                  <td>
                    {wallet.branchCode
                      ? getBranchName(wallet.branchCode, wallet.companyCode)
                      : "-"}
                  </td>

                  <td>
                    <span
                      className={`badge rounded-pill ${
                        wallet.walletType === "COMPANY"
                          ? "bg-primary"
                          : "bg-success"
                      }`}
                    >
                       {wallet.walletType === "BRANCH" ? "Agency" : wallet.walletType}
                    </span>
                  </td>

                  <td>₹ {wallet.balance?.toLocaleString("en-IN")}</td>

                  {showTopupButton && (
                  <td className="text-center">
                {wallet.walletType === "COMPANY"
                    ? "-"
                    : showTopupButton && (
                        <div className="d-flex gap-1 justify-content-center">
                        <button
                        className="btn btn-sm btn-success"
                        onClick={() => {
                            setSelectedWallet(wallet);
                            setShowTopupModal(true);
                        }}
                        >
                        <i className="bi bi-plus-circle"></i> TopUp
                        </button>
                        <button
                        className="btn btn-sm btn-warning"
                        onClick={() => {
                            setSelectedWithdrawWallet(wallet);
                            setShowWithdrawModal(true);
                        }}
                        >
                        <i className="bi bi-dash-circle"></i> Withdraw
                        </button>
                        </div>
                    )}
                </td>
                  )}

                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center text-muted">
                  No wallet records found.
                </td>
              </tr>
            )}
          </tbody>

        </table>
      </div>

      {/* ===================== PAGINATION ===================== */}
      {filteredWallets.length > rowsPerPage && (
        <div className="d-flex justify-content-center mt-3">
          <div className="d-flex align-items-center" style={{ gap: "5px" }}>

            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
            >
              Prev
            </button>

            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                className={`btn btn-sm ${
                  currentPage === i + 1
                    ? "btn-primary"
                    : "btn-outline-primary"
                }`}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}

            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() =>
                setCurrentPage((p) => Math.min(p + 1, totalPages))
              }
              disabled={currentPage === totalPages}
            >
              Next
            </button>

          </div>
        </div>
      )}

      {/* ===================== TOPUP MODAL ===================== */}
      {showTopupModal && (
        <div className="modal show fade d-block">
          <div className="modal-dialog">
            <div className="modal-content">

              <div className="modal-header">
                <h5 className="modal-title">Raise Wallet TopUp Request</h5>
                <button
                  className="btn-close"
                  onClick={() => setShowTopupModal(false)}
                ></button>
              </div>

              <div className="modal-body">

                <div className="mb-3">
                  <label className="form-label">Amount</label>
                  <input
                    type="String"
                    className="form-control"
                    value={topupAmount}
                    onChange={(e) => setTopupAmount(e.target.value)}
                  />
                </div>

                <div className="mb-3">
                <label className="form-label">UTR Number</label>
               <input
                type="text"
                className="form-control"
                value={utrNumber}
                onChange={(e) => setUtrNumber(e.target.value.toUpperCase())}
              />
              </div>

              <div className="mb-3">
                <label className="form-label">Account Holder Name</label>
               <input
                type="text"
                className="form-control"
                value={accountHolderName}
                onChange={(e) =>
                  setAccountHolderName(e.target.value.replace(/[^a-zA-Z\s]/g, ""))
                }
              />
              </div>

              </div>

              <div className="modal-footer">

                <button
                  className="btn btn-secondary"
                  onClick={() => setShowTopupModal(false)}
                >
                  Cancel
                </button>

                <button
                  className="btn btn-success"
                  onClick={raiseTopupRequest}
                >
                  Raise Request
                </button>

              </div>

            </div>
          </div>
        </div>
      )}
      {/* ===================== WITHDRAW MODAL ===================== */}
      {showWithdrawModal && (
        <div className="modal show fade d-block">
          <div className="modal-dialog">
            <div className="modal-content">

              <div className="modal-header">
                <h5 className="modal-title">Raise Withdrawal Request</h5>
                <button
                  className="btn-close"
                  onClick={() => setShowWithdrawModal(false)}
                ></button>
              </div>

              <div className="modal-body">

                <div className="mb-3">
                  <label className="form-label">Amount</label>
                  <input
                    type="number"
                    className="form-control"
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Remarks</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={withdrawRemarks}
                    onChange={(e) => setWithdrawRemarks(e.target.value)}
                  />
                </div>

              </div>

              <div className="modal-footer">

                <button
                  className="btn btn-secondary"
                  onClick={() => setShowWithdrawModal(false)}
                >
                  Cancel
                </button>

                <button
                  className="btn btn-warning"
                  onClick={raiseWithdrawRequest}
                >
                  Raise Withdrawal
                </button>

              </div>

            </div>
          </div>
        </div>
      )}

      <ToastContainer
  position="top-right"
  autoClose={3000}
  newestOnTop
  closeOnClick
  pauseOnHover
  draggable
/>

    </div>
  );
};

export default WalletList;